import clsx from 'clsx'

import { ChildProps } from '@/common/util/props'

import Icon, { IconColor, IconType } from '@/components/view/atoms/icon/Icon'
import ExternalLink from '@/components/view/utils/link/ExternalLink'
import InternalLink from '@/components/view/utils/link/InternalLink'

interface Props extends ChildProps {
  type?: 'submit' | 'reset' | 'button'
  size?: 10 | 12 | 14 | 16
  color?: 'link' | 'critical' | 'primary' | 'on-bg-fill'
  bold?: boolean
  iconColor?: IconColor
  link?: string
  noInternal?: boolean
  disabled?: boolean
  newTab?: boolean
  downloadIcon?: boolean
  truncate?: boolean
  disclosure?: boolean
  onClick?(e: React.MouseEvent<HTMLElement>): void
  download?: string
  nowrap?: boolean
  nospace?: boolean
  loading?: boolean
  arrow?: boolean
}

const TextButton = ({
  children,
  type = 'button',
  size = 14,
  color = 'link',
  bold = false,
  link,
  noInternal = false,
  newTab = false,
  downloadIcon = false,
  disabled = false,
  truncate = false,
  disclosure = false,
  onClick,
  download,
  nowrap = false,
  nospace = false,
  loading = false,
  arrow = false
}: Props) => {
  const Tag = download || noInternal ? 'a' : newTab ? ExternalLink : InternalLink
  const iconColor = color == 'link' || color == 'primary' ? IconColor.EMPHASIS : IconColor.CRITICAL
  const className = clsx(
    `text-left disabled:text-disabled group cursor-pointer items-center disabled:pointer-events-none rounded `,
    disabled || loading
      ? 'text-disabled pointer-events-none'
      : `text-${color} outline-default  focus-visible:-outline-offset-4 hover:text-${color}-hover active:text-${color}-active`,
    `text-size-${size}`,
    bold && 'font-bold',
    nowrap ? 'whitespace-nowrap' : 'whitespace-normal',
    truncate ? 'flex truncate w-full' : 'inline-block',
    !nospace ? 'px-1 py-2 -mx-1 -my-2 leading-120' : 'leading-150'
  )
  return (
    <>
      {link ? (
        <Tag
          className={className}
          link={link}
          href={link}
          tabIndex={disabled ? -1 : 0}
          download={download}
          onClick={onClick}
        >
          <span className={clsx('hover:underline', truncate && 'block truncate')}>{children}</span>
          {disclosure && (
            <Icon
              type={IconType.DOWN}
              color={disabled ? IconColor.DISABLED : IconColor.EMPHASIS}
              hoverColor={disabled ? IconColor.DISABLED : IconColor.EMPHASIS_HOVER}
              activeColor={disabled ? IconColor.DISABLED : IconColor.EMPHASIS_ACTIVE}
            />
          )}
          {newTab && (
            <Icon
              color={disabled ? IconColor.DISABLED : iconColor}
              hoverColor={`${iconColor}-hover`}
              activeColor={`${iconColor}-active`}
              type={IconType.NEW_TAB}
            />
          )}
          {arrow && (
            <Icon
              color={disabled ? IconColor.DISABLED : iconColor}
              hoverColor={`${iconColor}-hover`}
              activeColor={`${iconColor}-active`}
              type={IconType.ARROW_RIGHT}
            />
          )}
        </Tag>
      ) : (
        <button
          type={type}
          className={clsx(className, 'inline-flex items-center gap-x-1')}
          onClick={onClick}
          tabIndex={disabled || loading ? -1 : 0}
        >
          <span
            className={clsx(
              'items-center gap-1 leading-100 hover:underline',
              truncate ? 'block truncate' : 'inline-flex'
            )}
          >
            {children}
            {loading ? (
              <Icon color={IconColor.SECONDARY} type={IconType.LOADING} spin />
            ) : disclosure ? (
              <Icon
                type={IconType.DOWN}
                color={disabled || loading ? IconColor.DISABLED : IconColor.EMPHASIS}
                hoverColor={disabled || loading ? IconColor.DISABLED : IconColor.EMPHASIS_HOVER}
                activeColor={disabled || loading ? IconColor.DISABLED : IconColor.EMPHASIS_ACTIVE}
              />
            ) : (
              downloadIcon && (
                <Icon
                  color={disabled || loading ? IconColor.DISABLED : iconColor}
                  hoverColor={`${iconColor}-hover`}
                  activeColor={`${iconColor}-active`}
                  type={IconType.DOWNLOAD}
                />
              )
            )}
            {arrow && (
              <Icon
                color={disabled ? IconColor.DISABLED : iconColor}
                hoverColor={`${iconColor}-hover`}
                activeColor={`${iconColor}-active`}
                type={IconType.ARROW_RIGHT}
              />
            )}
          </span>
        </button>
      )}
    </>
  )
}

export default TextButton
